<script setup lang="ts">
/** Page de création d'une demande d'accompagnement — brief structuré. */
import { computed, reactive, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useToast } from 'vue-toast-notification'
import { ArrowLeftIcon, UserGroupIcon } from '@heroicons/vue/24/outline'
import { useAccompagnementStore } from '@/v2/accompagnement/store/useAccompagnementStore'
import { useBoostCorePermissionStore } from '@/stores/useBoostCorePermissionStore'
import { canCreate } from '@/v2/accompagnement/helpers/accompagnementPermissions'
import type {
  AccompClientRef,
  AccompContext,
  AccompDocument,
  AccompProjectConstraints,
  AccompRequestType,
  AccompScope,
  AccompUrgency,
} from '@/v2/accompagnement/api/accompagnementTypes'
import AccompCreateFormAdmin from '@/v2/accompagnement/components/AccompCreateFormAdmin.vue'
import AccompCreateFormDemande from '@/v2/accompagnement/components/AccompCreateFormDemande.vue'
import AccompCreateFormObjective from '@/v2/accompagnement/components/AccompCreateFormObjective.vue'
import AccompCreateFormContext from '@/v2/accompagnement/components/AccompCreateFormContext.vue'
import AccompCreateFormScope from '@/v2/accompagnement/components/AccompCreateFormScope.vue'
import AccompCreateFormProjectConstraints from '@/v2/accompagnement/components/AccompCreateFormProjectConstraints.vue'
import AccompCreateFormDocuments from '@/v2/accompagnement/components/AccompCreateFormDocuments.vue'

const router = useRouter()
const store = useAccompagnementStore()
const perms = useBoostCorePermissionStore()
const toast = useToast()

const allowed = computed(() => canCreate(perms))

const form = reactive({
  client: null as AccompClientRef | null,
  requestType: '' as AccompRequestType | '',
  urgency: 'NORMAL' as AccompUrgency,
  estimatedHours: null as number | null,
  objective: '',
  context: {
    activity: '',
    sites: null,
    collaborators: null,
    remoteWork: '',
    growth: '',
    constraints: '',
  } as AccompContext,
  scope: [] as AccompScope[],
  projectConstraints: {
    desiredDate: null,
    mandatoryDate: null,
    budgetKnown: false,
    budgetAmount: null,
    decisionMaker: '',
    competitorCount: null,
    tender: false,
  } as AccompProjectConstraints,
  availableDocuments: [] as AccompDocument[],
})

const submitted = ref(false)

// Le secteur d'activité (libellé NAF récupéré via le picker) alimente « Activité du
// client » s'il est encore vide — sans écraser une saisie manuelle.
watch(
  () => form.client?.nafLabel,
  (label) => {
    if (label && !form.context.activity.trim()) form.context.activity = label
  },
)

const errors = computed(() => ({
  client: submitted.value && !form.client ? 'Sélectionnez un client.' : undefined,
  requestType: submitted.value && !form.requestType ? 'Choisissez un type de demande.' : undefined,
  objective: submitted.value && !form.objective.trim() ? "L'objectif du client est requis." : undefined,
}))

const valid = computed(() => !!form.client && !!form.requestType && !!form.objective.trim())

async function submit() {
  submitted.value = true
  if (!valid.value || !form.client) return
  const res = await store.create({
    client: form.client,
    requestType: form.requestType as AccompRequestType,
    urgency: form.urgency,
    estimatedHours: form.estimatedHours,
    objective: form.objective.trim(),
    context: form.context,
    scope: form.scope,
    projectConstraints: form.projectConstraints,
    availableDocuments: form.availableDocuments,
  })
  if (res) {
    toast.success(`Demande ${res.reference} créée.`)
    router.push(`/accompagnement/${res.id}`)
  } else {
    toast.error(store.actionError ?? 'Échec de la création de la demande.')
  }
}
</script>

<template>
  <div class="mx-auto w-full max-w-7xl p-4 md:p-6 lg:p-8">
    <RouterLink to="/accompagnement" class="mb-3 inline-flex items-center gap-1 text-sm text-base-content/70 hover:text-primary">
      <ArrowLeftIcon class="h-4 w-4" aria-hidden="true" />
      Retour aux demandes
    </RouterLink>

    <header class="mb-6">
      <h1 class="flex items-center gap-2 text-2xl font-bold">
        <UserGroupIcon class="h-6 w-6 text-primary" aria-hidden="true" />
        Nouvelle demande d'accompagnement
      </h1>
      <p class="mt-1 text-base-content/70">
        Adressez une demande à la technique pour l'un de vos clients.
      </p>
    </header>

    <div v-if="!allowed" role="alert" class="alert alert-warning">
      <span>Vous n'avez pas les droits pour créer une demande d'accompagnement.</span>
    </div>

    <form
      v-else
      class="grid grid-cols-1 gap-5 md:grid-cols-2 md:items-stretch"
      novalidate
      @submit.prevent="submit"
    >
      <AccompCreateFormAdmin v-model:client="form.client" :client-error="errors.client" />
      <AccompCreateFormContext v-model:context="form.context" />
      <AccompCreateFormDemande
        v-model:request-type="form.requestType"
        v-model:urgency="form.urgency"
        v-model:estimated-hours="form.estimatedHours"
        :type-error="errors.requestType"
      />
      <AccompCreateFormObjective v-model:objective="form.objective" :objective-error="errors.objective" />
      <AccompCreateFormScope v-model:scope="form.scope" class="md:col-span-2" />
      <AccompCreateFormProjectConstraints v-model:constraints="form.projectConstraints" />
      <AccompCreateFormDocuments v-model:documents="form.availableDocuments" />

      <div class="flex items-center justify-end gap-2 md:col-span-2">
        <RouterLink to="/accompagnement" class="btn btn-ghost">Annuler</RouterLink>
        <button type="submit" class="btn btn-primary" :disabled="store.actionLoading" :aria-busy="store.actionLoading">
          <span v-if="store.actionLoading" class="loading loading-spinner loading-xs" aria-hidden="true"></span>
          Créer la demande
        </button>
      </div>
    </form>
  </div>
</template>
